"use client"

import { Box } from "@chakra-ui/react/box"
import { type ReactNode, useEffect, useState } from "react"
import bash from "react-syntax-highlighter/dist/esm/languages/prism/bash"
import css from "react-syntax-highlighter/dist/esm/languages/prism/css"
import javascript from "react-syntax-highlighter/dist/esm/languages/prism/javascript"
import json from "react-syntax-highlighter/dist/esm/languages/prism/json"
import jsx from "react-syntax-highlighter/dist/esm/languages/prism/jsx"
import markup from "react-syntax-highlighter/dist/esm/languages/prism/markup"
import scss from "react-syntax-highlighter/dist/esm/languages/prism/scss"
import sql from "react-syntax-highlighter/dist/esm/languages/prism/sql"
import tsx from "react-syntax-highlighter/dist/esm/languages/prism/tsx"
import typescript from "react-syntax-highlighter/dist/esm/languages/prism/typescript"
import yaml from "react-syntax-highlighter/dist/esm/languages/prism/yaml"
import SyntaxHighlighter from "react-syntax-highlighter/dist/esm/prism-light"

// Register the limited set of languages we actually use.
// Switching from full `react-syntax-highlighter` (uses `refractor` with all
// languages, ~1.4 MB) to PrismAsyncLight + manual registration drops the
// bundle by ~1.1 MB.
SyntaxHighlighter.registerLanguage("bash", bash)
SyntaxHighlighter.registerLanguage("sh", bash)
SyntaxHighlighter.registerLanguage("shell", bash)
SyntaxHighlighter.registerLanguage("css", css)
SyntaxHighlighter.registerLanguage("html", markup)
SyntaxHighlighter.registerLanguage("javascript", javascript)
SyntaxHighlighter.registerLanguage("js", javascript)
SyntaxHighlighter.registerLanguage("json", json)
SyntaxHighlighter.registerLanguage("jsx", jsx)
SyntaxHighlighter.registerLanguage("markup", markup)
SyntaxHighlighter.registerLanguage("scss", scss)
SyntaxHighlighter.registerLanguage("sql", sql)
SyntaxHighlighter.registerLanguage("tsx", tsx)
SyntaxHighlighter.registerLanguage("typescript", typescript)
SyntaxHighlighter.registerLanguage("ts", typescript)
SyntaxHighlighter.registerLanguage("xml", markup)
SyntaxHighlighter.registerLanguage("yaml", yaml)
SyntaxHighlighter.registerLanguage("yml", yaml)

interface CodeBlockProps {
  children: ReactNode
  containerProps?: React.ComponentProps<typeof Box>
  highlighterProps?: Record<string, unknown>
  language: string
}

const CodeBlock = ({
  children,
  containerProps,
  highlighterProps,
  language,
}: CodeBlockProps) => {
  // Lazy-load the theme so it doesn't ship in the initial chunk.
  const [style, setStyle] = useState<Record<string, React.CSSProperties>>({})

  useEffect(() => {
    let cancelled = false
    import("react-syntax-highlighter/dist/esm/styles/prism/one-dark")
      .then((mod) => {
        if (!cancelled) {
          setStyle(mod.default)
        }
      })
      .catch(() => {
        // theme load failed — fall back to default styling
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Box my={4} {...containerProps} className="markdown-code-block-container">
      <SyntaxHighlighter
        language={language}
        PreTag="div"
        style={style}
        {...highlighterProps}
      >
        {String(children).replace(/\n$/, "")}
      </SyntaxHighlighter>
    </Box>
  )
}

export default CodeBlock
